import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

const PaymentTest = () => {
  const [gateways, setGateways] = useState([]);
  const [selectedGateway, setSelectedGateway] = useState(null);
  const [testData, setTestData] = useState({
    amount: 100,
    customerName: 'Test Customer',
    customerEmail: 'test@example.com',
    customerPhone: '9876543210'
  });
  const [paymentUrl, setPaymentUrl] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchGateways();
  }, []);

  const fetchGateways = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/payment-gateways/active`);
      setGateways(response.data.gateways || []);
    } catch (error) {
      console.error('Error fetching gateways:', error);
    }
  };

  const generateTestPayment = async () => {
    if (!selectedGateway) {
      alert('Please select a payment gateway');
      return;
    }

    setLoading(true);
    try {
      const paymentData = {
        orderId: 'TEST' + Date.now(),
        amount: testData.amount,
        customerName: testData.customerName,
        customerEmail: testData.customerEmail,
        customerPhone: testData.customerPhone,
        description: 'Test Payment'
      };

      const response = await axios.post(`${API_BASE_URL}/api/payment/generate-url`, {
        gatewayId: selectedGateway._id,
        paymentData
      });

      if (response.data.success) {
        setPaymentUrl(response.data.paymentUrl);
      } else {
        alert('Failed to generate payment URL');
      }
    } catch (error) {
      alert('Error: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const openPayment = () => {
    if (paymentUrl) {
      window.open(paymentUrl, '_blank');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Payment Gateway Test</h1>
          <p className="text-gray-600">Test payment integrations</p>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-bold mb-6">Payment Test Configuration</h2>
          
          {/* Gateway Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Payment Gateway
            </label>
            <div className="space-y-2">
              {gateways.map(gateway => (
                <div
                  key={gateway._id}
                  onClick={() => setSelectedGateway(gateway)}
                  className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                    selectedGateway?._id === gateway._id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="font-medium">{gateway.name}</div>
                  <div className="text-sm text-gray-600">{gateway.type.toUpperCase()}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Test Data Form */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Amount (₹)</label>
              <input
                type="number"
                value={testData.amount}
                onChange={(e) => setTestData({...testData, amount: parseFloat(e.target.value)})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name</label>
              <input
                type="text"
                value={testData.customerName}
                onChange={(e) => setTestData({...testData, customerName: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={testData.customerEmail}
                onChange={(e) => setTestData({...testData, customerEmail: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input
                type="tel"
                value={testData.customerPhone}
                onChange={(e) => setTestData({...testData, customerPhone: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Generate Button */}
          <button
            onClick={generateTestPayment}
            disabled={!selectedGateway || loading}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white py-3 rounded-lg font-medium transition-colors mb-4"
          >
            {loading ? 'Generating...' : 'Generate Payment URL'}
          </button>

          {/* Payment URL Display */}
          {paymentUrl && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="font-semibold text-green-800 mb-2">Payment URL Generated!</h3>
              <div className="bg-white p-3 rounded border text-sm font-mono break-all mb-3">
                {paymentUrl}
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={openPayment}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg"
                >
                  Open Payment Page
                </button>
                <button
                  onClick={() => navigator.clipboard.writeText(paymentUrl)}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg"
                >
                  Copy URL
                </button>
              </div>
            </div>
          )}

          {/* Test Instructions */}
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-blue-800 mb-2">Test Instructions</h3>
            <div className="text-sm text-blue-700 space-y-1">
              <p>1. Select a payment gateway from the list above</p>
              <p>2. Configure test amount and customer details</p>
              <p>3. Click "Generate Payment URL" to create payment link</p>
              <p>4. Click "Open Payment Page" to test the payment flow</p>
              <p>5. Use test card details for ICICI UAT: 4111111111111111</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentTest;